import { useState, useRef, useEffect } from 'react';
import { categories } from '@/mocks/categories';
import { Link } from 'react-router-dom';

export default function Hero() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(e.target as Node)) {
        setMobileMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleMobileNavClick = () => {
    setMobileMenuOpen(false);
  };

  return (
    <header className="relative w-full min-h-screen flex flex-col">
      <div className="absolute inset-0 z-0">
        <img
          src="https://readdy.ai/api/search-image?query=artistic%20flat%20lay%20composition%20of%20designer%20sneakers%20streetwear%20hoodies%20caps%20and%20accessories%20arranged%20on%20warm%20cream%20textured%20surface%2C%20soft%20diffused%20daylight%20from%20large%20window%2C%20minimalist%20Scandinavian%20aesthetic%2C%20warm%20beige%20and%20sand%20tones%2C%20editorial%20product%20photography%2C%20harmonious%20balanced%20composition%20with%20negative%20space&width=1600&height=900&seq=hero-main-v2&orientation=landscape"
          alt="OOPBUY Spreadsheet Produktübersicht"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/50"></div>
      </div>

      <nav className="relative z-20 w-full px-4 md:px-12 py-4 md:py-5 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 cursor-pointer">
          <span className="text-white font-heading text-lg md:text-2xl font-bold tracking-tight whitespace-nowrap">OOPBUY Spreadsheet</span>
        </Link>

        <div className="hidden md:flex items-center gap-6 lg:gap-8">
          <Link to="/" className="text-white/90 text-sm font-medium hover:text-white transition-colors whitespace-nowrap cursor-pointer">Startseite</Link>

          <div ref={dropdownRef} className="relative">
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className="flex items-center gap-1.5 text-white/90 text-sm font-medium hover:text-white transition-colors whitespace-nowrap cursor-pointer"
            >
              Kategorien
              <span className={`w-4 h-4 flex items-center justify-center transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}>
                <i className="ri-arrow-down-s-line text-sm"></i>
              </span>
            </button>

            {dropdownOpen && (
              <div className="absolute top-full mt-3 left-0 w-64 bg-white rounded-xl py-2 z-50 max-h-80 overflow-y-auto shadow-lg">
                {categories.map((cat) => (
                  <a
                    key={cat.id}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-800 hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <span className="w-5 h-5 flex items-center justify-center">
                      <i className={`${cat.icon} text-foreground-500`}></i>
                    </span>
                    {cat.nameDe}
                  </a>
                ))}
              </div>
            )}
          </div>

          <a href="#categories" className="text-white/90 text-sm font-medium hover:text-white transition-colors whitespace-nowrap">Beliebte Kategorien</a>
          <Link to="/oopbuy-spreadsheet-guide" className="text-white/90 text-sm font-medium hover:text-white transition-colors whitespace-nowrap cursor-pointer">Shopping Guide</Link>
          <a href="#articles" className="text-white/90 text-sm font-medium hover:text-white transition-colors whitespace-nowrap">Artikel</a>
        </div>

        <div className="flex items-center gap-2 md:gap-3">
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="hidden sm:inline-flex items-center gap-1.5 px-4 md:px-5 py-2 md:py-2.5 bg-white/15 backdrop-blur-sm text-white text-xs md:text-sm font-semibold rounded-full border border-white/25 hover:bg-white/25 transition-all whitespace-nowrap cursor-pointer"
          >
            oopbuy spreadsheet
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-up-line text-sm"></i>
            </span>
          </a>
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center text-white cursor-pointer"
            aria-label="Menü öffnen"
          >
            <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-xl`}></i>
          </button>
        </div>
      </nav>

      {mobileMenuOpen && (
        <div ref={mobileMenuRef} className="fixed inset-0 z-40 md:hidden">
          <div className="absolute inset-0 bg-black/60" onClick={() => setMobileMenuOpen(false)}></div>
          <div className="absolute top-0 right-0 w-80 h-full bg-white overflow-y-auto shadow-2xl">
            <div className="p-5">
              <div className="flex items-center justify-between mb-6">
                <span className="font-heading text-lg font-bold text-foreground-900">Menü</span>
                <button onClick={() => setMobileMenuOpen(false)} className="w-8 h-8 flex items-center justify-center cursor-pointer">
                  <i className="ri-close-line text-xl text-foreground-700"></i>
                </button>
              </div>

              <div className="mb-6">
                <Link to="/" onClick={handleMobileNavClick} className="flex items-center gap-3 px-3 py-3 text-foreground-900 font-semibold hover:bg-background-100 rounded-lg transition-colors cursor-pointer">
                  <span className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-home-4-line text-lg"></i>
                  </span>
                  Startseite
                </Link>
                <a href="#categories" onClick={handleMobileNavClick} className="flex items-center gap-3 px-3 py-3 text-foreground-700 font-medium hover:bg-background-100 rounded-lg transition-colors cursor-pointer">
                  <span className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-grid-line text-lg"></i>
                  </span>
                  Beliebte Kategorien
                </a>
                <Link to="/oopbuy-spreadsheet-guide" onClick={handleMobileNavClick} className="flex items-center gap-3 px-3 py-3 text-foreground-700 font-medium hover:bg-background-100 rounded-lg transition-colors cursor-pointer">
                  <span className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-book-open-line text-lg"></i>
                  </span>
                  Shopping Guide
                </Link>
                <a href="#articles" onClick={handleMobileNavClick} className="flex items-center gap-3 px-3 py-3 text-foreground-700 font-medium hover:bg-background-100 rounded-lg transition-colors cursor-pointer">
                  <span className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-article-line text-lg"></i>
                  </span>
                  Artikel
                </a>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={handleMobileNavClick}
                  className="flex items-center gap-3 px-3 py-3 text-primary-600 font-semibold hover:bg-primary-50 rounded-lg transition-colors cursor-pointer"
                >
                  <span className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-shopping-bag-3-line text-lg"></i>
                  </span>
                  oopbuy spreadsheet &rarr;
                </a>
              </div>

              <div className="border-t border-background-200 pt-5">
                <p className="text-xs text-foreground-500 font-semibold uppercase tracking-wider mb-3 px-3">Kategorien</p>
                <div className="space-y-0.5">
                  {categories.map((cat) => (
                    <a
                      key={cat.id}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      onClick={handleMobileNavClick}
                      className="flex items-center gap-3 px-3 py-2.5 text-sm text-foreground-700 hover:bg-background-100 rounded-lg transition-colors cursor-pointer"
                    >
                      <span className="w-6 h-6 flex items-center justify-center rounded-full bg-background-100">
                        <i className={`${cat.icon} text-xs text-foreground-600`}></i>
                      </span>
                      {cat.nameDe}
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="relative z-10 flex-1 flex flex-col justify-center px-4 md:px-12 lg:px-20 w-full">
        <div className="max-w-2xl">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-white/12 backdrop-blur-sm rounded-full border border-white/20 mb-6">
            <span className="w-1.5 h-1.5 rounded-full bg-accent-400"></span>
            <span className="text-white/85 text-xs md:text-sm font-medium whitespace-nowrap">2026 OOPBUY Spreadsheet Guide</span>
          </div>

          <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-bold text-white leading-tight mb-5">
            OOPBUY Spreadsheet<br />
            <span className="text-white/85">Deutschland</span>
          </h1>

          <p className="text-white/75 text-sm md:text-base leading-relaxed max-w-lg mb-8">
            Die unabhängige deutsche Informationsplattform für OOPBUY Spreadsheet, Produktlisten, Verkäufervergleiche und aktuelle Shopping-Tipps an einem Ort.
          </p>

          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 px-7 py-3.5 bg-primary-500 text-white text-sm font-semibold rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              Jetzt entdecken
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line text-sm"></i>
              </span>
            </a>
            <Link
              to="/oopbuy-spreadsheet-guide"
              className="inline-flex items-center gap-2 px-7 py-3.5 bg-white/12 backdrop-blur-sm text-white text-sm font-medium rounded-full border border-white/25 hover:bg-white/20 transition-all whitespace-nowrap cursor-pointer"
            >
              Guide lesen
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-book-open-line text-sm"></i>
              </span>
            </Link>
          </div>

          <div className="flex flex-wrap items-center gap-4 md:gap-6 mt-10">
            <div className="flex items-center gap-2">
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-shield-check-line text-accent-400 text-sm"></i>
              </span>
              <span className="text-white/70 text-xs whitespace-nowrap">Geprüfte Verkäufer</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-refresh-line text-accent-400 text-sm"></i>
              </span>
              <span className="text-white/70 text-xs whitespace-nowrap">Aktualisierte Listen</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-global-line text-accent-400 text-sm"></i>
              </span>
              <span className="text-white/70 text-xs whitespace-nowrap">Für DE, AT, CH</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-guide-line text-accent-400 text-sm"></i>
              </span>
              <span className="text-white/70 text-xs whitespace-nowrap">Deutsche Guides</span>
            </div>
          </div>
        </div>
      </div>

      <div className="relative z-10 w-full flex justify-center pb-8">
        <a href="#categories" className="flex flex-col items-center gap-2 text-white/60 hover:text-white/90 transition-colors cursor-pointer">
          <span className="text-xs font-medium whitespace-nowrap">Entdecken</span>
          <span className="w-5 h-5 flex items-center justify-center animate-bounce">
            <i className="ri-arrow-down-line text-lg"></i>
          </span>
        </a>
      </div>
    </header>
  );
}